import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  CopySimpleIcon,
  DotsThreeIcon,
  GraduationCapIcon,
  PencilSimpleIcon,
  PlusIcon,
  TrashIcon,
} from '@phosphor-icons/react';

import {
  SCOPE_LABELS,
  createSkill,
  deleteSkill,
  forkSystemSkill,
  isValidSkillName,
  seedSkillMd,
  setSystemSkillEnabled,
  useAvailableSkills,
  type AvailableSkill,
  type SkillScope,
} from '@/api-models/skills';
import { useAuth } from '@/contexts/auth-context';
import { useSpace } from '@/contexts/space-context';
import { settingsBase } from '@/features/settings/components/nav-config';
import { ReadOnlyTag } from '@/features/settings/components/ReadOnlyTag';
import { Badge } from '@/ui/Badge';
import { Button, IconButton } from '@/ui/button/Button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/ui/DropdownMenu';
import { Input } from '@/ui/Input';
import { Modal } from '@/ui/Modal';
import { SegmentedControl } from '@/ui/SegmentedControl';
import { Spinner } from '@/ui/Spinner';
import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from '@/ui/Table';
import { Textarea } from '@/ui/Textarea';
import { formatTimeAgo } from '@/utils/format';

/**
 * Settings → Skills.
 *
 * A skill is a folder of instructions (and, later, scripts and references) the agent loads on
 * demand when a task matches its description. Skills live at three scopes — personal, team, and a
 * staff-managed system tier every space sees read-only.
 *
 * The scope control has a third segment here, unlike Playbooks. System skills need somewhere to
 * host their per-team on/off switch and the Fork action, and folding them into the same table as
 * editable skills would mean branching every affordance per row — edit vs view, delete vs disable —
 * which is where this kind of list rots.
 */
export function SkillsSection() {
  const { me } = useAuth();
  const { activeTeam, teamId } = useSpace();
  const navigate = useNavigate();

  const teamSpace = activeTeam && !activeTeam.is_personal ? activeTeam : null;
  const [scope, setScope] = useState<SkillScope>(teamSpace ? 'team' : 'personal');

  // Switching spaces changes which scopes exist; follow the space.
  useEffect(() => {
    setScope(teamSpace ? 'team' : 'personal');
  }, [teamSpace]);

  // `available` is answered by whichever team the settings page is scoped to — it already folds in
  // the user's personal skills and the system tier, so one call covers every segment.
  const { skills, mutate, isLoading } = useAvailableSkills(teamId);
  const [creating, setCreating] = useState(false);

  const visible = useMemo(() => skills.filter((s) => s.scope === scope), [skills, scope]);
  const systemCount = useMemo(() => skills.filter((s) => s.scope === 'system').length, [skills]);

  // Writes always target a real team: the user's own for personal scope, the active one otherwise.
  const writeTeamId = scope === 'personal' ? me.personalTeamId : teamId;

  async function handleDelete(skill: AvailableSkill) {
    if (!skill.team_id) return;
    try {
      await deleteSkill(skill.team_id, skill.id);
      await mutate();
      toast.success(`Deleted "${skill.name}"`);
    } catch {
      toast.error('Could not delete skill');
    }
  }

  async function handleToggleSystem(skill: AvailableSkill, enabled: boolean) {
    try {
      await setSystemSkillEnabled(teamId, skill.id, enabled);
      await mutate();
      toast.success(enabled ? `Enabled "${skill.name}"` : `Disabled "${skill.name}" for this space`);
    } catch {
      toast.error('Could not change this skill');
    }
  }

  async function handleFork(skill: AvailableSkill) {
    try {
      const forked = await forkSystemSkill(teamId, skill.id);
      await mutate();
      toast.success(`Forked "${skill.name}" — the copy starts disabled`);
      navigate(`${settingsBase(teamId)}/automation/skills/${forked.id}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not fork this skill');
    }
  }

  const scopeOptions: { value: SkillScope; label: string }[] = [
    { value: 'personal', label: 'Personal' },
    ...(teamSpace ? [{ value: 'team' as const, label: 'Team' }] : []),
    { value: 'system', label: systemCount > 0 ? `System (${systemCount})` : 'System' },
  ];

  return (
    <div className="flex flex-col gap-7">
      {/* Header */}
      <div className="flex items-start justify-between gap-6">
        <div className="flex flex-col">
          <h1 className="page-title">Skills</h1>
          <p className="page-subtitle">
            Procedures the agent loads on demand — how to open a PR here, how to add a migration, whatever your team
            does the same way every time.
          </p>
        </div>
        {scope !== 'system' && (
          <Button
            type="button"
            variant="primary"
            size={9}
            onClick={() => setCreating(true)}
            iconLeft={<PlusIcon size={16} weight="bold" />}
          >
            New skill
          </Button>
        )}
      </div>

      {/* Scope controls */}
      <div className="flex items-center gap-3">
        <SegmentedControl<SkillScope>
          value={scope}
          onChange={setScope}
          ariaLabel="Skill scope"
          options={scopeOptions}
        />
        <span className="text-xs text-fg-tertiary">{scopeHint(scope, teamSpace?.name)}</span>
      </div>

      {/* List */}
      <div className="overflow-hidden rounded-xl border border-line bg-surface-1">
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell className="w-32">Status</TableHeaderCell>
              <TableHeaderCell className="w-40">Updated</TableHeaderCell>
              <TableHeaderCell className="w-12" />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && skills.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-fg-tertiary">
                  <span className="inline-flex items-center gap-2">
                    <Spinner size={4} /> Loading skills…
                  </span>
                </TableCell>
              </TableRow>
            ) : visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-fg-tertiary">
                  {emptyMessage(scope)}
                </TableCell>
              </TableRow>
            ) : (
              visible.map((skill) => (
                <SkillRow
                  key={skill.id}
                  skill={skill}
                  href={`${settingsBase(teamId)}/automation/skills/${skill.id}`}
                  onDelete={() => handleDelete(skill)}
                  onToggle={(enabled) => handleToggleSystem(skill, enabled)}
                  onFork={() => handleFork(skill)}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <p className="text-xs text-fg-tertiary">
        Skills are loaded by the agent when a task matches their description. Changes apply to new agent sessions — a
        session already running keeps the set it started with.
      </p>

      {creating && (
        <NewSkillModal
          teamId={writeTeamId}
          existingNames={visible.map((s) => s.name)}
          onClose={() => setCreating(false)}
          onCreated={async (id) => {
            await mutate();
            navigate(`${settingsBase(teamId)}/automation/skills/${id}`);
          }}
        />
      )}
    </div>
  );
}

function scopeHint(scope: SkillScope, teamName?: string): string {
  if (scope === 'system') return 'Maintained by Zooid staff. Fork one to customise it, or turn it off for this space.';
  if (scope === 'team') return `Shared with everyone in ${teamName ?? 'this team'}.`;
  return 'Only your own agent sessions use these.';
}

function emptyMessage(scope: SkillScope): string {
  if (scope === 'system') return 'No system skills are published yet.';
  return 'No skills yet. Create one to teach your agents a procedure they should follow every time.';
}

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

function SkillRow({
  skill,
  href,
  onDelete,
  onToggle,
  onFork,
}: {
  skill: AvailableSkill;
  href: string;
  onDelete: () => void;
  onToggle: (enabled: boolean) => void;
  onFork: () => void;
}) {
  const navigate = useNavigate();

  return (
    <TableRow>
      <TableCell>
        <button type="button" className="flex min-w-0 items-center gap-2.5 text-left" onClick={() => navigate(href)}>
          <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-surface-3 text-fg-secondary">
            <GraduationCapIcon size={16} />
          </span>
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-sm font-semibold text-fg">{skill.name}</span>
            <span className="truncate text-xs text-fg-tertiary">{skill.description}</span>
          </span>
        </button>
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap items-center gap-1.5">
          {skill.read_only && <ReadOnlyTag />}
          {!skill.enabled && (
            <Badge variant="neutral" size="sm">
              Off
            </Badge>
          )}
          {/* A skill silently losing to a higher-precedence one of the same name is otherwise
              invisible — the agent just uses the other. */}
          {skill.shadowed_by && (
            <Badge variant="warning" size="sm">
              Overridden by {SCOPE_LABELS[skill.shadowed_by].toLowerCase()}
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell className="font-medium text-fg-secondary">{formatTimeAgo(skill.updated_at)}</TableCell>
      <TableCell>
        <div className="flex justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton size={8} aria-label={`Actions for ${skill.name}`}>
                <DotsThreeIcon size={18} weight="bold" />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => navigate(href)}>
                <PencilSimpleIcon size={16} />
                {skill.read_only ? 'View' : 'Edit'}
              </DropdownMenuItem>
              {skill.read_only ? (
                <>
                  <DropdownMenuItem onSelect={onFork}>
                    <CopySimpleIcon size={16} />
                    Fork into this space
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => onToggle(!skill.enabled)}>
                    {skill.enabled ? 'Turn off here' : 'Turn on here'}
                  </DropdownMenuItem>
                </>
              ) : (
                <DropdownMenuItem destructive onSelect={onDelete}>
                  <TrashIcon size={16} />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  );
}

/**
 * Creation asks only for a name and a description, then opens the editor.
 *
 * The description is the only thing the model sees when deciding whether to reach for a skill, so
 * it gets a labelled field with its own guidance rather than being buried in a YAML block the user
 * has to know to write.
 */
function NewSkillModal({
  teamId,
  existingNames,
  onClose,
  onCreated,
}: {
  teamId: string;
  existingNames: string[];
  onClose: () => void;
  onCreated: (skillId: string) => void | Promise<void>;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const trimmedName = name.trim();
  const nameError =
    trimmedName && !isValidSkillName(trimmedName)
      ? 'Lowercase letters, digits, and single hyphens only — e.g. open-a-pr.'
      : existingNames.includes(trimmedName)
        ? 'A skill with this name already exists here.'
        : null;
  const canSave = Boolean(trimmedName) && !nameError && Boolean(description.trim()) && !saving;

  async function handleSave() {
    if (!canSave) return;
    setSaving(true);
    try {
      const created = await createSkill(teamId, {
        skill_md: seedSkillMd(trimmedName, description.trim()),
      });
      toast.success(`Created "${created.name}"`);
      await onCreated(created.id);
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not create skill');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="New skill"
      description="You'll write the instructions next."
      maxWidth="max-w-xl"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSave();
        }}
        className="flex flex-col gap-4 px-6 pb-6"
      >
        <div className="flex flex-col gap-1.5">
          <label htmlFor="skill-name" className="text-xs font-medium text-fg">
            Name
          </label>
          <Input
            id="skill-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="open-a-pr"
            autoFocus
          />
          {nameError ? (
            <span className="text-2xs leading-3.5 text-danger">{nameError}</span>
          ) : (
            <span className="text-2xs leading-3.5 text-fg-tertiary">How the agent refers to this skill.</span>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="skill-description" className="text-xs font-medium text-fg">
            When should the agent use this?
          </label>
          <Textarea
            id="skill-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="h-24 text-xs leading-5"
            placeholder="Opens a pull request the way this repo expects. Use when asked to ship, open a PR, or push a branch."
          />
          <span className="text-2xs leading-3.5 text-fg-tertiary">
            All the agent sees when choosing a skill — name the trigger, not just the topic.
          </span>
        </div>

        <div className="mt-1 flex justify-end gap-2">
          <Button type="button" variant="secondary" size={9} onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" size={9} disabled={!canSave} isLoading={saving}>
            Create and edit
          </Button>
        </div>
      </form>
    </Modal>
  );
}
